import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ProgressStepBarService, StepModel } from './progress-step-bar.service';
import { Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

interface Step {
  number: number;
  title: string;
  subtitle: string;
  status: 'completed' | 'active' | 'pending';
}


@Component({
  selector: 'app-progress-step-bar',
  templateUrl: './progress-step-bar.component.html',
  styleUrl: './progress-step-bar.component.css'
})

export class ProgressStepBarComponent implements OnInit, OnDestroy {
  // @Input() salesId?: number | null;
  // @Input() startDate?: string;
  // @Input() endDate?: string;
  @Input() autoLoad: boolean = true;
  startDate: string = '';
  endDate: string = '';
  salesId: number = 0;

  steps: StepModel[] = [];
  isLoading: boolean = false;
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private progressService: ProgressStepBarService
  ) {}

  ngOnInit(): void {
    this.salesId = Number(this.route.snapshot.paramMap.get('id')) || 0;

    const startDate = localStorage.getItem('startDate')
    const endDate = localStorage.getItem('endDate')

    this.startDate = startDate ? startDate : '';
    this.endDate = endDate ? endDate : '';

    if (this.autoLoad && this.salesId) {
      this.loadProgressSteps();
    }

    // Subscribe to steps changes
    this.progressService.getSteps()
      .pipe(takeUntil(this.destroy$))
      .subscribe(steps => {
        this.steps = steps;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load progress steps from API
  loadProgressSteps(): void {
    if (!this.salesId) {
      this.error = 'Sales ID is required';
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.progressService.loadProgressSteps(this.salesId, this.startDate, this.endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (steps) => {
          this.steps = steps;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading progress steps:', error);
          this.error = 'Failed to load progress steps';
          this.isLoading = false;
        }
      });
  }

  // Reload progress steps
  reload(): void {
    this.loadProgressSteps();
  }

  // Get completion percentage
  getCompletionPercentage(): number {
    return this.progressService.getCompletionPercentage();
  }

  // Check if step is completed
  isStepCompleted(step: StepModel): boolean {
    return step.status === 'completed';
  }

  // Check if step is active
  isStepActive(step: StepModel): boolean {
    return step.status === 'in-progress';
  }

  // Check if step is pending
  isStepPending(step: StepModel): boolean {
    return step.status === 'pending';
  }

  // Get step status class
  getStepStatusClass(step: StepModel): string {
    switch (step.status) {
      case 'completed':
        return 'completed';
      case 'in-progress':
        return 'active';
      case 'pending':
        return '';
      case 'error':
        return 'error';
      default:
        return '';
    }
  }

  // Get status display text
  getStatusDisplayText(step: StepModel): string {
    switch (step.status) {
      case 'completed':
        return 'Completed';
      case 'in-progress':
        return 'In Progress';
      case 'pending':
        return 'Pending';
      case 'error':
        return 'Error';
      default:
        return 'Pending';
    }
  }

  // Get status CSS class for text
  getStatusTextClass(step: StepModel): string {
    switch (step.status) {
      case 'completed':
        return 'completed';
      case 'in-progress':
        return 'in-progress';
      case 'pending':
        return 'pending';
      case 'error':
        return 'error';
      default:
        return 'pending';
    }
  }
}
